
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BulletinTimeDisplay from './BulletinTimeDisplay';
import BulletinAuthor from './BulletinAuthor';
import BulletinActions from './BulletinActions';
import { isSocialMediaBrowser } from '@/utils/browser-detection';

interface BulletinInteractionLayerProps {
  bulletin: any;
  hasLiked: boolean;
  likeCount: number;
  isSelected: boolean;
  onDoubleClick: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
}

const BulletinInteractionLayer = ({ 
  bulletin, 
  hasLiked,
  likeCount, 
  isSelected,
  onDoubleClick,
  onClick
}: BulletinInteractionLayerProps) => {
  const lastTapRef = useRef(0);
  const lastTapPositionRef = useRef({ x: 0, y: 0 });
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartRef = useRef<{ x: number, y: number } | null>(null);
  const hasMoveRef = useRef(false);
  const isInSocialBrowserRef = useRef(false);
  
  useEffect(() => {
    // Check if we're in a social media browser
    isInSocialBrowserRef.current = isSocialMediaBrowser();
    
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  const isSimilarPosition = (x1: number, y1: number, x2: number, y2: number) => {
    const threshold = 30;
    return Math.abs(x1 - x2) < threshold && Math.abs(y1 - y2) < threshold;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const now = Date.now();
    const timeDiff = now - lastTapRef.current;
    
    const touchX = touch.clientX;
    const touchY = touch.clientY;
    
    // Store initial position for movement detection
    touchStartRef.current = { x: touchX, y: touchY };
    hasMoveRef.current = false;
    
    // If in social media browser, we'll handle this specially in touchEnd
    if (isInSocialBrowserRef.current) {
      return;
    }
    
    if (timeDiff < 500 && isSimilarPosition(
      touchX, 
      touchY, 
      lastTapPositionRef.current.x, 
      lastTapPositionRef.current.y
    )) {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
      console.log('[InteractionLayer] Double tap detected');
      onDoubleClick(e as unknown as React.MouseEvent);
    }
    
    lastTapRef.current = now;
    lastTapPositionRef.current = { x: touchX, y: touchY };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    
    const touch = e.touches[0];
    const moveX = Math.abs(touch.clientX - touchStartRef.current.x);
    const moveY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // If movement is greater than threshold, consider it a scroll
    const moveThreshold = 10; // pixels
    if (moveX > moveThreshold || moveY > moveThreshold) {
      hasMoveRef.current = true;
      
      // Clear any potential interaction timeout
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
        interactionTimeoutRef.current = null;
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // If in social media browser and no movement, count as a tap
    if (isInSocialBrowserRef.current && !hasMoveRef.current && onClick) {
      const fakeEvent = { target: e.target } as unknown as React.MouseEvent;
      onClick(fakeEvent);
    }
    
    // Reset touch tracking
    touchStartRef.current = null;
    
    // Don't process tap if there was movement
    if (hasMoveRef.current) {
      hasMoveRef.current = false;
      return;
    }
  };

  return (
    <>
      {/* Always render the interaction layer covering the entire card */}
      <div 
        className="absolute inset-0 cursor-pointer"
        style={{ zIndex: 50 }} // Consistent z-index of 50
        onDoubleClick={(e) => {
          console.log('[InteractionLayer] Double click event');
          onDoubleClick(e);
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove} // Add touch move handling
        onTouchEnd={handleTouchEnd} // Add touch end handling
        aria-label="Double tap to like"
        data-bulletin-id={bulletin.id}
        data-liked={hasLiked ? 'true' : 'false'}
      />

      <motion.div 
        className="absolute top-0 left-0 mt-3 ml-3"
        style={{
          position: 'absolute',
          transform: 'none',
          zIndex: 40 // Place below interaction layer
        }}
      >
        <BulletinTimeDisplay 
          createdAt={bulletin.created_at}
          expiresAt={bulletin.expires_at}
          isUpdate={bulletin.is_update}
        />
      </motion.div>
    </>
  );
};

export default BulletinInteractionLayer;
